import { useLocation, useParams } from "react-router";
import { connect, useSelector } from "react-redux";
import { State } from "redux/store";
import { useEffect, useMemo, useState } from "react";

import {
  getUserConfig,
  getTotalPagesForData,
  getEntityTypeFilter,
  getPaginationMetadataForData,
  getPaginationMetadataForCluster,
  getTotalPagesForCluster,
  getIsCardslistLoading,
  getMetadataCount,
} from "redux/SingleDataset/selectors";

import { formatNumberWithCommas } from "helpers/utility/formatters";

import GranularitySlider from "./GranularitySlider";
import Paginator from "./Paginator";
import { FilterOption, isValidEntityType } from "types";
import InfoBar from "./InfoBar";

import styles from "./style.module.scss";
import {
  ENTITY_TYPE_PARAMETER,
  REVIEW_PARAMETER,
  PAGE_PARAMETER,
} from "helpers/constants/miscellaneous";
import useQueryParam from "hooks/useQueryParam";

const StatsFooter = ({
  userConfig,
  totalPagesForData,
  totalPagesForCluster,
  metadataForData,
  metadataForCluster,
  isCardListLoading,
  reviewCounters,
}: any) => {
  const { datasetId, clusterId } = useParams();
  const [countText, setCountText] = useState<string | React.ReactNode>();
  const isClusterPage = datasetId && clusterId;
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const entityParam = useQueryParam(ENTITY_TYPE_PARAMETER);
  const entityTypeFromQueryParam = isValidEntityType(entityParam)
    ? entityParam
    : null;
  const entityType = useSelector((state: State) =>
    getEntityTypeFilter(state, entityTypeFromQueryParam),
  );
  const entityCount = useSelector((state) =>
    getMetadataCount(state, null, !isClusterPage),
  );

  const selectedEntity =
    entityType.find((entityType: FilterOption) => entityType.selected)?.text ||
    "Images";

  const entityCountText = `Showing ${formatNumberWithCommas(
    entityCount || 0,
  )} ${entityCount === 1 ? selectedEntity.slice(0, -1) : selectedEntity}`;
  const metadata = isClusterPage ? metadataForCluster : metadataForData;
  const pageNumber = parseInt(useQueryParam(PAGE_PARAMETER) || "1");
  const inReview = searchParams.has(REVIEW_PARAMETER);
  useEffect(() => {
    if (inReview) {
      setCountText(reviewCounters);
    } else {
      setCountText(entityCountText);
    }
  }, [entityCountText, inReview, reviewCounters]);

  // Use review count text when in review mode and available, otherwise use regular entity count
  const totalPages = isClusterPage ? totalPagesForCluster : totalPagesForData;
  //changes based on assumpmtion that explorations stats will only have "ANY" context
  const statsBehavior = userConfig?.find(
    (config: any) => config.feature_key === "EXPLORATION_STATS",
  )?.feature_behavior["ANY"];
  const showStats = statsBehavior === "SHOW" ? true : false;

  const selectedEntityType = entityType.find((emtity: any) => emtity.selected);

  const dynamicType = isClusterPage
    ? selectedEntityType
      ? selectedEntityType.text
      : "Images"
    : "CLUSTERS";

  const itemsPerPage = metadata ? metadata.size : 0;
  const totalItems = metadata ? metadata.total : 0;

  const currentBracket = useMemo(() => {
    const startingValue =
      pageNumber === 1 ? 1 : (pageNumber - 1) * itemsPerPage + 1;
    const endingValue = Math.min(startingValue + itemsPerPage - 1, totalItems);

    return `${
      endingValue === 0 ? 0 : formatNumberWithCommas(startingValue)
    } - ${formatNumberWithCommas(endingValue)}`;
  }, [pageNumber, itemsPerPage, totalItems]);

  return (
    <div
      className={styles.footerContainer}
      style={isCardListLoading ? { pointerEvents: "none" } : undefined}
    >
      {isCardListLoading ? (
        <div className={styles.countLoader}></div>
      ) : (
        <div className={styles.entityCount}>{countText}</div>
      )}
      <InfoBar showStats={showStats} />
      {!inReview && metadata && (
        <Paginator
          totalCount={totalItems}
          currentBracket={currentBracket}
          type={dynamicType}
          totalPages={totalPages}
          currentPage={pageNumber}
        />
      )}
      <GranularitySlider />
    </div>
  );
};

function mapStatesToProps(state: State) {
  return {
    userConfig: getUserConfig(state),
    totalPagesForData: getTotalPagesForData(state),
    totalPagesForCluster: getTotalPagesForCluster(state),
    metadataForData: getPaginationMetadataForData(state),
    metadataForCluster: getPaginationMetadataForCluster(state),
    isCardListLoading: getIsCardslistLoading(state),
  };
}

export default connect(mapStatesToProps)(StatsFooter);
